import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faMagnifyingGlass, faRotate } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import "./PanelComplianceNuevo.css";
import { CompliancePanelContext } from "../../context/CompliancePanelContext";
import { getToken } from "../../utils/auth";


const FILAS_ALERTAS_POR_PAGINA = 10;
const EXPORTACIONES_TRANSACCIONALES = [
    { tipo: "mes", label: "Excel mes" },
    { tipo: "contracargos", label: "Excel contracargos" },
    { tipo: "inusuales", label: "Excel inusuales" },
    { tipo: "sospechosas", label: "Excel sospechosas" },
];

const PanelComplianceNuevo = ({ darkMode = false }) => {
    const {
        resultadosCache,
        detalleCache,
        transaccionalCache,
        guardarResultados,
        guardarDetalle,
        guardarTransaccional,
    } = useContext(CompliancePanelContext);
    const [termino, setTermino] = useState("");
    const [datos, setDatos] = useState([]);
    const [seleccionado, setSeleccionado] = useState(null);
    const [detalle, setDetalle] = useState(null);
    const [transaccional, setTransaccional] = useState(null);
    const [loadingBusqueda, setLoadingBusqueda] = useState(false);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [loadingTransaccional, setLoadingTransaccional] = useState(false);
    const [loadingExportacion, setLoadingExportacion] = useState("");
    const [origenActivo, setOrigenActivo] = useState("alta");
    const [paginaActual, setPaginaActual] = useState(1);
    const [paginaSospechosas, setPaginaSospechosas] = useState(1);
    const [paginaInusuales, setPaginaInusuales] = useState(1);

    const token = getToken();

    const buscar = useCallback(async (terminoActual = "") => {
        if (!token) {
            return;
        }

        const terminoNormalizado = (terminoActual || "").trim();
        const cacheKey = `${origenActivo}::${terminoNormalizado.toLowerCase()}`;
        const cacheado = resultadosCache[cacheKey];

        if (cacheado) {
            setDatos(cacheado);
            setPaginaActual(1);
            return;
        }

        setLoadingBusqueda(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_COMPLIANCE_PANEL_BUSCAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token, Termino: terminoNormalizado, Origen: origenActivo }),
            });

            if (response.status === 401 || response.status === 403) {
                throw new Error("Su sesion ya no es valida.");
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const result = await response.json();
            const lista = Array.isArray(result) ? result : [];
            setDatos(lista);
            guardarResultados(cacheKey, lista);
            setPaginaActual(1);
        } catch (error) {
            console.error("Error al buscar compliance:", error);
            Swal.fire("Error", error.message || "No se pudo cargar la busqueda.", "error");
        } finally {
            setLoadingBusqueda(false);
        }
    }, [guardarResultados, origenActivo, resultadosCache, token]);

    const cargarDetalle = useCallback(async (item) => {
        if (!token || !item) {
            return;
        }

        const cacheKey = `${item.origen}-${item.idRegistro}`;
        setSeleccionado(item);

        if (detalleCache[cacheKey]) {
            setDetalle(detalleCache[cacheKey]);
            return;
        }

        setLoadingDetalle(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_COMPLIANCE_PANEL_DETALLE, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    Origen: item.origen,
                    IdRegistro: item.idRegistro,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                throw new Error("Su sesion ya no es valida.");
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const result = await response.json();
            setDetalle(result || null);
            guardarDetalle(cacheKey, result || null);
        } catch (error) {
            console.error("Error al obtener detalle compliance:", error);
            setDetalle(null);
            Swal.fire("Error", error.message || "No se pudo cargar la ficha.", "error");
        } finally {
            setLoadingDetalle(false);
        }
    }, [detalleCache, guardarDetalle, token]);

    const cargarTransaccional = useCallback(async (item, filtroMes = null) => {
        if (!token || !item) {
            return;
        }

        const keyMes = filtroMes?.anio && filtroMes?.mes
            ? `${filtroMes.anio}-${String(filtroMes.mes).padStart(2, "0")}`
            : "ultimo";
        const terminalCacheKey = normalizarCacheKey(item?.nroTerminal || item?.nroComercio || "sin-terminal");
        const cacheKey = `${item.origen}-${item.idRegistro}-${terminalCacheKey}-${keyMes}`;

        if (transaccionalCache[cacheKey]) {
            setTransaccional(transaccionalCache[cacheKey]);
            return;
        }

        setLoadingTransaccional(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_COMPLIANCE_TX_RESUMEN, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    Origen: item.origen,
                    IdRegistro: item.idRegistro,
                    Anio: filtroMes?.anio || null,
                    Mes: filtroMes?.mes || null,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                throw new Error("Su sesion ya no es valida.");
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const result = await response.json();
            setTransaccional(result || null);
            guardarTransaccional(cacheKey, result || null);
        } catch (error) {
            console.error("Error al obtener datos transaccionales:", error);
            setTransaccional(null);
            Swal.fire("Error", error.message || "No se pudo cargar el comportamiento transaccional.", "error");
        } finally {
            setLoadingTransaccional(false);
        }
    }, [guardarTransaccional, token, transaccionalCache]);

    useEffect(() => {
        buscar(termino);
    }, [buscar]);

    useEffect(() => {
        if (!seleccionado) {
            return;
        }

        const itemActualizado = datos.find(
            (item) => item.origen === seleccionado.origen && item.idRegistro === seleccionado.idRegistro
        );

        if (!itemActualizado) {
            setSeleccionado(null);
            setDetalle(null);
        }
    }, [datos, seleccionado]);

    useEffect(() => {
        if (!seleccionado) {
            setTransaccional(null);
            return;
        }

        const hoy = new Date();
        setTransaccional(null);
        cargarTransaccional(seleccionado, {
            anio: hoy.getFullYear(),
            mes: hoy.getMonth() + 1,
        });
    }, [cargarTransaccional, seleccionado]);

    useEffect(() => {
        setPaginaSospechosas(1);
        setPaginaInusuales(1);
    }, [transaccional?.anioSeleccionado, transaccional?.mesSeleccionado, seleccionado?.idRegistro, seleccionado?.origen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        await buscar(termino.trim());
    };

    const cambiarOrigen = async (nuevoOrigen) => {
        if (nuevoOrigen === origenActivo) {
            return;
        }

        setOrigenActivo(nuevoOrigen);
        setPaginaActual(1);
    };

    const descargarDocumento = async (documento) => {
        if (!token || !documento?.idDocumento || !documento?.idLegajo) {
            return;
        }

        try {
            const response = await fetch(process.env.REACT_APP_API_DOCLEGAJO_DESCARGAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    IdLegajo: documento.idLegajo,
                    IdDocumento: documento.idDocumento,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const data = await response.json();
            const base64 = data?.base64 || data?.Base64;
            if (!base64) {
                throw new Error("No se pudo descargar el documento.");
            }

            const mime = documento.documentoTipo || "application/octet-stream";
            const href = `data:${mime};base64,${base64}`;
            const link = document.createElement("a");
            link.href = href;
            link.download = documento.nombreDocumento || "documento";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error al descargar documento:", error);
            Swal.fire("Error", error.message || "No se pudo descargar el documento.", "error");
        }
    };

    const descargarExcelTransaccional = useCallback(async (tipoExportacion) => {
        if (!token || !seleccionado || !transaccional?.anioSeleccionado || !transaccional?.mesSeleccionado) {
            return;
        }

        setLoadingExportacion(tipoExportacion);
        try {
            const response = await fetch(process.env.REACT_APP_API_COMPLIANCE_TX_EXCEL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    Origen: seleccionado.origen,
                    IdRegistro: seleccionado.idRegistro,
                    Anio: transaccional.anioSeleccionado,
                    Mes: transaccional.mesSeleccionado,
                    TipoExportacion: tipoExportacion,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                throw new Error("Su sesion ya no es valida.");
            }

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `Error ${response.status}`);
            }

            const blob = await response.blob();
            const fileName = obtenerNombreArchivo(response.headers.get("content-disposition"))
                || `compliance_${tipoExportacion}.xlsx`;

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al exportar transaccional:", error);
            Swal.fire("Error", error.message || "No se pudo exportar el Excel.", "error");
        } finally {
            setLoadingExportacion("");
        }
    }, [seleccionado, token, transaccional]);

    const cuitsAsociados = useMemo(() => {
        const items = [...(detalle?.cuitsVinculados || []), detalle?.resumen?.cuit]
            .filter(Boolean)
            .map((item) => item.trim());

        return Array.from(new Set(items));
    }, [detalle]);

    const resumenMetricas = useMemo(() => {
        if (!detalle) {
            return [];
        }

        return [
            { label: "Origen", value: formatearOrigen(detalle.resumen?.origen), tone: "neutral" },
            { label: "Estado", value: detalle.resumen?.estado || "-", tone: resolverEstadoTone(detalle.resumen?.estado) },
            { label: "Modalidad", value: detalle.operativa?.modalidadOperativa || "-", tone: "neutral" },
            { label: "Legajo", value: detalle.legajoRelacionado?.nroLegajo || detalle.resumen?.legajo || "-", tone: "neutral" },
            { label: "Documentos", value: `${detalle.documentos?.length || 0}`, tone: "neutral" },
            { label: "Seguimientos", value: `${detalle.seguimientos?.length || 0}`, tone: "neutral" },
        ];
    }, [detalle]);

    const bloques = useMemo(() => {
        if (!detalle) {
            return [];
        }

        return [
            {
                titulo: "Operacion",
                descripcion: "Datos operativos y de alta del comercio.",
                valores: [
                    ["Estado", detalle.operativa?.estado],
                    ["Modalidad", detalle.operativa?.modalidadOperativa],
                    ["Terminal", detalle.operativa?.nDeTerminal],
                    ["Comercio", detalle.operativa?.nDeComercio],
                    ["Nro. local", detalle.operativa?.nroLocal],
                    ["Legajo", detalle.operativa?.legajo],
                    ["Fantasia", detalle.operativa?.nombreDeFantasia],
                    ["Domicilio", detalle.operativa?.domicilio],
                    ["Localidad", detalle.operativa?.localidad],
                    ["Provincia", detalle.operativa?.provincia],
                    ["Codigo postal", detalle.operativa?.codigoPostal],
                    ["Telefono", detalle.operativa?.telefono],
                    ["Rubro", detalle.operativa?.rubro],
                    ["Rubro secundario", detalle.operativa?.rubroSecundario],
                    ["Ahora 12", detalle.operativa?.altaAhora12],
                    ["Fecha alta", formatearFecha(detalle.operativa?.fechaDeAlta)],
                    ["Fecha baja", formatearFecha(detalle.operativa?.fechaDeBaja)],
                    ["Tipo baja", detalle.operativa?.tipoDeBaja],
                    ["Asesor", detalle.operativa?.nombreDelAsesor],
                    ["Autorizado", detalle.operativa?.autorizadoPorTitular],
                    ["Observacion", detalle.operativa?.observacion],
                ],
            },
            {
                titulo: "Titular y fiscal",
                descripcion: "Titularidad, mail y estado fiscal del comercio.",
                valores: [
                    ["Titular", detalle.titular?.titular],
                    ["CUIT/CUIL", detalle.titular?.cuitCuil],
                    ["Razon social", detalle.titular?.razonSocial],
                    ["CUIT razon social", detalle.titular?.cuitRazonSocial],
                    ["Mail", detalle.titular?.mail],
                    ["Estado AFIP", detalle.fiscales?.estadoAfip],
                    ["Cod. actividad", detalle.fiscales?.codActividad],
                    ["Actividad", detalle.fiscales?.actividad],
                    ["Exento", detalle.fiscales?.exento],
                    ["Estado rentas", detalle.fiscales?.estadoRentas],
                    ["Actividad 2", detalle.fiscales?.actividad2],
                    ["Domicilio fiscal", detalle.fiscales?.domicilioFiscal],
                    ["Provincia fiscal", detalle.fiscales?.provinciaFiscal],
                    ["Alicuota", detalle.fiscales?.alicuota],
                    ["Cod. actividad rentas", detalle.fiscales?.codActividadRentas],
                    ["Actividad rentas", detalle.fiscales?.actividadRentas],
                ],
            },
            {
                titulo: "Bancario",
                descripcion: "Cuenta y datos bancarios declarados en el alta.",
                valores: [
                    ["Banco", detalle.bancarios?.banco],
                    ["Tipo de cuenta", detalle.bancarios?.tipoDeCuenta],
                    ["CBU/CVU", detalle.bancarios?.cbuCvu],
                    ["Nro. cuenta", detalle.bancarios?.nDeCuenta],
                    ["Letra CBU", detalle.bancarios?.letraCbu],
                    ["Largo CBU", detalle.bancarios?.largoCbu],
                    ["Alias", detalle.bancarios?.alias],
                ],
            },
        ];
    }, [detalle]);

    const legajoDetalle = useMemo(() => {
        if (!detalle?.legajoRelacionado) {
            return [];
        }

        const legajo = detalle.legajoRelacionado;
        return [
            ["Id legajo", legajo.id],
            ["Nro. legajo", legajo.nroLegajo],
            ["Terminal", legajo.terminal],
            ["Fantasia", legajo.nombreDeFantasia],
            ["Tipo persona", legajo.tipoPersona],
            ["CUIT titular/sociedad", legajo.cuitTitularOSociedad],
            ["Razon social", legajo.razonSocial],
            ["Domicilio", construirDomicilioLegajo(legajo)],
            ["Localidad", legajo.localidad],
            ["Provincia", legajo.provincia],
            ["Codigo postal", legajo.codigoPostal],
            ["Email contacto", legajo.emailContacto],
            ["Celular / WhatsApp", legajo.celularWhatsapp],
            ["Fecha carga", formatearFecha(legajo.fechaDeCarga)],
            ["Asesor", legajo.asesor],
        ];
    }, [detalle]);

    const totalPaginas = Math.max(1, Math.ceil(datos.length / 10));

    const datosPaginados = useMemo(() => {
        const inicio = (paginaActual - 1) * 10;
        return datos.slice(inicio, inicio + 10);
    }, [datos, paginaActual]);

    const rangoPaginacion = useMemo(() => {
        const inicio = Math.max(1, paginaActual - 2);
        const fin = Math.min(totalPaginas, inicio + 4);
        const paginas = [];

        for (let pagina = inicio; pagina <= fin; pagina += 1) {
            paginas.push(pagina);
        }

        return paginas;
    }, [paginaActual, totalPaginas]);

    const metricasTransaccionales = useMemo(() => {
        if (!transaccional?.tieneDatos) {
            return [];
        }

        return [
            { label: "Volumen", value: formatearMoneda(transaccional.volumenTransacciones), tone: "neutral" },
            { label: "Operaciones", value: transaccional.cantidadOperacionesMensuales, tone: "neutral" },
            { label: "Frecuencia", value: `${transaccional.frecuenciaDiasOperados} dias`, tone: "neutral" },
            { label: "Ticket promedio", value: formatearMoneda(transaccional.ticketPromedio), tone: "warning" },
            { label: "Monto maximo", value: formatearMoneda(transaccional.montoMaximo), tone: "warning" },
            { label: "Contracargos", value: transaccional.cantidadContracargos, tone: transaccional.cantidadContracargos > 0 ? "danger" : "positive" },
            { label: "Ops. inusuales", value: transaccional.cantidadOperacionesInusuales, tone: transaccional.cantidadOperacionesInusuales > 0 ? "warning" : "positive" },
            { label: "Ops. sospechosas", value: transaccional.cantidadOperacionesSospechosas, tone: transaccional.cantidadOperacionesSospechosas > 0 ? "danger" : "positive" },
        ];
    }, [transaccional]);

    const operacionesSospechosas = transaccional?.operacionesSospechosas || [];
    const operacionesInusuales = transaccional?.operacionesInusuales || [];

    const totalPaginasSospechosas = Math.max(1, Math.ceil(operacionesSospechosas.length / FILAS_ALERTAS_POR_PAGINA));
    const totalPaginasInusuales = Math.max(1, Math.ceil(operacionesInusuales.length / FILAS_ALERTAS_POR_PAGINA));

    const operacionesSospechosasPaginadas = useMemo(
        () => obtenerItemsPaginados(operacionesSospechosas, paginaSospechosas, FILAS_ALERTAS_POR_PAGINA),
        [operacionesSospechosas, paginaSospechosas]
    );

    const operacionesInusualesPaginadas = useMemo(
        () => obtenerItemsPaginados(operacionesInusuales, paginaInusuales, FILAS_ALERTAS_POR_PAGINA),
        [operacionesInusuales, paginaInusuales]
    );

    const paginasSospechosas = useMemo(
        () => obtenerRangoPaginas(totalPaginasSospechosas, paginaSospechosas),
        [paginaSospechosas, totalPaginasSospechosas]
    );

    const paginasInusuales = useMemo(
        () => obtenerRangoPaginas(totalPaginasInusuales, paginaInusuales),
        [paginaInusuales, totalPaginasInusuales]
    );

    return (
        <section className={`compliance-panel-wrap ${darkMode ? "is-dark" : ""}`}>
            <div className="compliance-hero">
                <div className="compliance-hero-copy">
                    <span className="compliance-kicker">Panel Compliance</span>
                    <h2 className="compliance-hero-title">Ficha consolidada de comercios y legajos</h2>
                    <p className="compliance-hero-text">
                        Consulta operativa, titularidad, fiscal, bancaria y documental en una sola vista.
                    </p>
                </div>
                <div className="compliance-hero-stats">
                    <div className="compliance-mini-stat">
                        <span className="compliance-mini-stat-label">Resultados</span>
                        <strong className="compliance-mini-stat-value">{datos.length}</strong>
                    </div>
                    <div className="compliance-mini-stat">
                        <span className="compliance-mini-stat-label">Seleccion</span>
                        <strong className="compliance-mini-stat-value">
                            {detalle?.resumen?.nombreFantasia || "Sin ficha"}
                        </strong>
                    </div>
                </div>
            </div>

            <div className="compliance-panel-toolbar">
                <Form className="compliance-search-form" onSubmit={handleSubmit}>
                    <Form.Control
                        value={termino}
                        onChange={(e) => setTermino(e.target.value)}
                        placeholder="Buscar por CUIT, terminal, legajo, fantasia, razon social o provincia"
                        className="compliance-panel-search"
                    />
                    <Button type="submit" className="compliance-action-btn" disabled={loadingBusqueda}>
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="me-2" />
                        Buscar
                    </Button>
                    <Button
                        type="button"
                        className="compliance-secondary-btn"
                        onClick={() => {
                            setTermino("");
                            buscar("");
                        }}
                        disabled={loadingBusqueda}
                    >
                        <FontAwesomeIcon icon={faRotate} className="me-2" />
                        Limpiar
                    </Button>
                </Form>
                <div className="compliance-toolbar-copy">
                    Busca por cualquier dato clave y entra directo a la ficha del comercio.
                </div>
            </div>

            <div className="compliance-panel-body">
                {loadingBusqueda ? (
                    <div className="compliance-loading">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Cargando resultados...
                    </div>
                ) : (
                    <div className="compliance-results-wrap mb-4">
                        <div className="compliance-results-head">
                            <div>
                                <div className="compliance-section-title">Resultados de busqueda</div>
                                <div className="compliance-section-subtitle">
                                    Altas se muestran por defecto. Cambia a bajas cuando necesites revisar el historico de bajas.
                                </div>
                            </div>
                            <div className="compliance-results-controls">
                                <div className="compliance-toggle-group">
                                    <button
                                        type="button"
                                        className={`compliance-toggle-btn ${origenActivo === "alta" ? "is-active" : ""}`}
                                        onClick={() => cambiarOrigen("alta")}
                                    >
                                        Altas
                                    </button>
                                    <button
                                        type="button"
                                        className={`compliance-toggle-btn ${origenActivo === "baja" ? "is-active" : ""}`}
                                        onClick={() => cambiarOrigen("baja")}
                                    >
                                        Bajas
                                    </button>
                                </div>
                                <span className="compliance-results-count">{datos.length} registros</span>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <Table hover className="mb-0 compliance-results-table">
                                <thead>
                                    <tr>
                                        <th>Origen</th>
                                        <th>Estado</th>
                                        <th>Terminal</th>
                                        <th>Comercio</th>
                                        <th>Legajo</th>
                                        <th>Fantasia</th>
                                        <th>CUIT</th>
                                        <th>Provincia</th>
                                        <th className="text-center">Ficha</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {datos.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center py-4 text-muted">
                                                No hay resultados para mostrar.
                                            </td>
                                        </tr>
                                    ) : (
                                        datosPaginados.map((item) => (
                                            <tr
                                                key={`${item.origen}-${item.idRegistro}`}
                                                className={
                                                    seleccionado?.origen === item.origen &&
                                                    seleccionado?.idRegistro === item.idRegistro
                                                        ? "compliance-row-active"
                                                        : ""
                                                }
                                            >
                                                <td>
                                                    <span className="compliance-chip compliance-chip-muted">
                                                        {formatearOrigen(item.origen)}
                                                    </span>
                                                </td>
                                                <td>{item.estado || "-"}</td>
                                                <td>{item.nroTerminal || "-"}</td>
                                                <td>{item.nroComercio || "-"}</td>
                                                <td>{item.legajo || "-"}</td>
                                                <td className="compliance-cell-strong">{item.nombreFantasia || "-"}</td>
                                                <td>{item.cuit || "-"}</td>
                                                <td>{item.provincia || "-"}</td>
                                                <td className="text-center compliance-table-action">
                                                    <Button
                                                        size="sm"
                                                        className="compliance-inline-btn"
                                                        onClick={() => cargarDetalle(item)}
                                                    >
                                                        Ver ficha
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        {datos.length > 10 && (
                            <div className="compliance-pagination">
                                <button
                                    type="button"
                                    className="compliance-page-btn"
                                    disabled={paginaActual === 1}
                                    onClick={() => setPaginaActual((prev) => Math.max(1, prev - 1))}
                                >
                                    Anterior
                                </button>
                                <div className="compliance-page-list">
                                    {rangoPaginacion.map((pagina) => (
                                        <button
                                            type="button"
                                            key={pagina}
                                            className={`compliance-page-btn ${pagina === paginaActual ? "is-active" : ""}`}
                                            onClick={() => setPaginaActual(pagina)}
                                        >
                                            {pagina}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="compliance-page-btn"
                                    disabled={paginaActual === totalPaginas}
                                    onClick={() => setPaginaActual((prev) => Math.min(totalPaginas, prev + 1))}
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {loadingDetalle ? (
                    <div className="compliance-loading">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Cargando ficha compliance...
                    </div>
                ) : !detalle ? (
                    <div className="compliance-empty">
                        Selecciona un registro para ver la ficha consolidada del comercio.
                    </div>
                ) : (
                    <div className="compliance-detail-shell">
                        <Row className="g-3 mb-3">
                            <Col xl={8}>
                                <Card className="compliance-section-card compliance-summary-card h-100">
                                    <Card.Body>
                                        <div className="compliance-summary-head">
                                            <div>
                                                <div className="compliance-section-title compliance-summary-title">
                                                    {detalle.resumen?.nombreFantasia || "Sin nombre de fantasia"}
                                                </div>
                                                <div className="compliance-summary-subtitle">
                                                    {detalle.resumen?.razonSocial || "Sin razon social"}
                                                </div>
                                            </div>
                                            <div className="compliance-summary-badges">
                                                <span className={`compliance-chip ${resolverEstadoClass(detalle.resumen?.estado)}`}>
                                                    {detalle.resumen?.estado || "Sin estado"}
                                                </span>
                                                <span className="compliance-chip compliance-chip-muted">
                                                    {detalle.operativa?.modalidadOperativa || "Sin modalidad"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="compliance-summary-grid">
                                            {resumenMetricas.map((metrica) => (
                                                <div key={metrica.label} className={`compliance-summary-metric is-${metrica.tone || "neutral"}`}>
                                                    <span className="compliance-summary-metric-label">{metrica.label}</span>
                                                    <strong className="compliance-summary-metric-value">{metrica.value || "-"}</strong>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="compliance-linked-wrap">
                                            <span className="compliance-linked-title">CUIT asociados</span>
                                            <div>
                                                {cuitsAsociados.map((cuit) => (
                                                    <span
                                                        key={cuit}
                                                        className={`compliance-chip ${cuit === detalle.resumen?.cuit ? "is-active" : "compliance-chip-muted"}`}
                                                    >
                                                        {cuit}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xl={4}>
                                <Card className="compliance-section-card compliance-highlight-card h-100">
                                    <Card.Body>
                                        <div className="compliance-section-title mb-2">Lectura rapida</div>
                                        <div className="compliance-section-subtitle mb-3">
                                            Bloque listo para ver situacion general y legajo relacionado sin abrir otras pantallas.
                                        </div>
                                        <div className="compliance-highlight-list">
                                            <div className="compliance-highlight-item">
                                                <span>CUIT principal</span>
                                                <strong>{detalle.resumen?.cuit || "-"}</strong>
                                            </div>
                                            <div className="compliance-highlight-item">
                                                <span>Terminal</span>
                                                <strong>{detalle.resumen?.nroTerminal || "-"}</strong>
                                            </div>
                                            <div className="compliance-highlight-item">
                                                <span>Comercio</span>
                                                <strong>{detalle.resumen?.nroComercio || "-"}</strong>
                                            </div>
                                            <div className="compliance-highlight-item">
                                                <span>Provincia</span>
                                                <strong>{detalle.resumen?.provincia || "-"}</strong>
                                            </div>
                                            <div className="compliance-highlight-item">
                                                <span>Fecha alta</span>
                                                <strong>{formatearFecha(detalle.resumen?.fechaDeAlta)}</strong>
                                            </div>
                                            <div className="compliance-highlight-item">
                                                <span>Fecha baja</span>
                                                <strong>{formatearFecha(detalle.resumen?.fechaDeBaja)}</strong>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        <Row className="g-3 mb-3">
                            <Col xl={12}>
                                <Card className="compliance-section-card">
                                    <Card.Body>
                                        <div className="compliance-results-head compliance-transaccional-head">
                                            <div>
                                                <div className="compliance-section-title mb-1">Comportamiento transaccional</div>
                                                <div className="compliance-section-subtitle">
                                                    Base Dashboard por comercio individual. Se carga al abrir la ficha y permite cambiar el mes.
                                                </div>
                                            </div>
                                            {!loadingTransaccional && transaccional?.mesesDisponibles?.length > 0 && (
                                                <div className="compliance-transaccional-controls">
                                                    {transaccional?.tieneDatos && (
                                                        <div className="compliance-transaccional-actions">
                                                            {EXPORTACIONES_TRANSACCIONALES.map((item) => (
                                                                <button
                                                                    key={item.tipo}
                                                                    type="button"
                                                                    className="compliance-inline-btn"
                                                                    onClick={() => descargarExcelTransaccional(item.tipo)}
                                                                    disabled={loadingExportacion === item.tipo}
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                                    {loadingExportacion === item.tipo ? "Exportando..." : item.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <Form.Select
                                                        className="compliance-month-select"
                                                        value={`${transaccional.anioSeleccionado}-${transaccional.mesSeleccionado}`}
                                                        onChange={(e) => {
                                                            const [anio, mes] = e.target.value.split("-");
                                                            cargarTransaccional(seleccionado, {
                                                                anio: Number(anio),
                                                                mes: Number(mes),
                                                            });
                                                        }}
                                                    >
                                                        {transaccional.mesesDisponibles.map((item) => (
                                                            <option key={`${item.anio}-${item.mes}`} value={`${item.anio}-${item.mes}`}>
                                                                {capitalizarTexto(item.etiqueta)}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </div>
                                            )}
                                        </div>

                                        {loadingTransaccional ? (
                                            <div className="compliance-loading mt-3">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Cargando comportamiento transaccional...
                                            </div>
                                        ) : !transaccional?.tieneDatos ? (
                                            <div className="compliance-empty mt-3">
                                                {transaccional?.mensaje || "No hay datos transaccionales para este comercio."}
                                            </div>
                                        ) : (
                                            <>
                                                <div className="compliance-transaccional-meta">
                                                    <span className="compliance-chip is-active">
                                                        {capitalizarTexto(transaccional.mesEtiqueta)}
                                                    </span>
                                                    <span className={`compliance-chip ${transaccional.cantidadOperacionesSospechosas > 0 ? "is-danger" : "compliance-chip-muted"}`}>
                                                        {transaccional.cantidadOperacionesSospechosas > 0 ? "Requiere revision" : "Sin alertas criticas"}
                                                    </span>
                                                </div>

                                                <div className="compliance-summary-grid compliance-transaccional-grid mt-3">
                                                    {metricasTransaccionales.map((metrica) => (
                                                        <div key={metrica.label} className={`compliance-summary-metric is-${metrica.tone || "neutral"}`}>
                                                            <span className="compliance-summary-metric-label">{metrica.label}</span>
                                                            <strong className="compliance-summary-metric-value">{metrica.value || "-"}</strong>
                                                        </div>
                                                    ))}
                                                </div>

                                                <Row className="g-3 mt-1">
                                                    <Col lg={6}>
                                                        <div className="compliance-mini-table-wrap">
                                                            <div className="compliance-section-title mb-1">Operaciones sospechosas</div>
                                                            <div className="compliance-section-subtitle mb-3">
                                                                Tickets 80% o mas por encima del promedio mensual.
                                                            </div>
                                                            {operacionesSospechosas.length > 0 ? (
                                                                <>
                                                                    <div className="table-responsive">
                                                                        <Table className="compliance-section-table mb-0" hover>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Fecha</th>
                                                                                    <th>Cupon</th>
                                                                                    <th>Tarjeta</th>
                                                                                    <th>Monto</th>
                                                                                    <th>Vs promedio</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {operacionesSospechosasPaginadas.map((item, index) => (
                                                                                    <tr key={`sos-${paginaSospechosas}-${index}-${item.nroCupon || "sin-cupon"}`}>
                                                                                        <td>{formatearFecha(item.fechaOperacion)}</td>
                                                                                        <td>{item.nroCupon || "-"}</td>
                                                                                        <td>{item.tarjeta || "-"}</td>
                                                                                        <td>{formatearMoneda(item.monto)}</td>
                                                                                        <td className="text-danger fw-bold">+{formatearPorcentaje(item.variacionPorcentual)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                    {totalPaginasSospechosas > 1 && (
                                                                        <div className="compliance-pagination compliance-table-pagination">
                                                                            <span>
                                                                                Pagina {paginaSospechosas} de {totalPaginasSospechosas}
                                                                            </span>
                                                                            <div className="compliance-page-list">
                                                                                <button
                                                                                    type="button"
                                                                                    className="compliance-page-btn"
                                                                                    onClick={() => setPaginaSospechosas((prev) => Math.max(1, prev - 1))}
                                                                                    disabled={paginaSospechosas === 1}
                                                                                >
                                                                                    Anterior
                                                                                </button>
                                                                                {paginasSospechosas.map((pagina) => (
                                                                                    <button
                                                                                        key={`pag-sos-${pagina}`}
                                                                                        type="button"
                                                                                        className={`compliance-page-btn ${paginaSospechosas === pagina ? "is-active" : ""}`}
                                                                                        onClick={() => setPaginaSospechosas(pagina)}
                                                                                    >
                                                                                        {pagina}
                                                                                    </button>
                                                                                ))}
                                                                                <button
                                                                                    type="button"
                                                                                    className="compliance-page-btn"
                                                                                    onClick={() => setPaginaSospechosas((prev) => Math.min(totalPaginasSospechosas, prev + 1))}
                                                                                    disabled={paginaSospechosas === totalPaginasSospechosas}
                                                                                >
                                                                                    Siguiente
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="compliance-empty">No se detectaron operaciones sospechosas en este mes.</div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                    <Col lg={6}>
                                                        <div className="compliance-mini-table-wrap">
                                                            <div className="compliance-section-title mb-1">Operaciones inusuales</div>
                                                            <div className="compliance-section-subtitle mb-3">
                                                                Tickets entre 60% y 79% por encima del promedio mensual.
                                                            </div>
                                                            {operacionesInusuales.length > 0 ? (
                                                                <>
                                                                    <div className="table-responsive">
                                                                        <Table className="compliance-section-table mb-0" hover>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Fecha</th>
                                                                                    <th>Cupon</th>
                                                                                    <th>Tarjeta</th>
                                                                                    <th>Monto</th>
                                                                                    <th>Vs promedio</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {operacionesInusualesPaginadas.map((item, index) => (
                                                                                    <tr key={`inu-${paginaInusuales}-${index}-${item.nroCupon || "sin-cupon"}`}>
                                                                                        <td>{formatearFecha(item.fechaOperacion)}</td>
                                                                                        <td>{item.nroCupon || "-"}</td>
                                                                                        <td>{item.tarjeta || "-"}</td>
                                                                                        <td>{formatearMoneda(item.monto)}</td>
                                                                                        <td className="text-warning fw-bold">+{formatearPorcentaje(item.variacionPorcentual)}</td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </Table>
                                                                    </div>
                                                                    {totalPaginasInusuales > 1 && (
                                                                        <div className="compliance-pagination compliance-table-pagination">
                                                                            <span>
                                                                                Pagina {paginaInusuales} de {totalPaginasInusuales}
                                                                            </span>
                                                                            <div className="compliance-page-list">
                                                                                <button
                                                                                    type="button"
                                                                                    className="compliance-page-btn"
                                                                                    onClick={() => setPaginaInusuales((prev) => Math.max(1, prev - 1))}
                                                                                    disabled={paginaInusuales === 1}
                                                                                >
                                                                                    Anterior
                                                                                </button>
                                                                                {paginasInusuales.map((pagina) => (
                                                                                    <button
                                                                                        key={`pag-inu-${pagina}`}
                                                                                        type="button"
                                                                                        className={`compliance-page-btn ${paginaInusuales === pagina ? "is-active" : ""}`}
                                                                                        onClick={() => setPaginaInusuales(pagina)}
                                                                                    >
                                                                                        {pagina}
                                                                                    </button>
                                                                                ))}
                                                                                <button
                                                                                    type="button"
                                                                                    className="compliance-page-btn"
                                                                                    onClick={() => setPaginaInusuales((prev) => Math.min(totalPaginasInusuales, prev + 1))}
                                                                                    disabled={paginaInusuales === totalPaginasInusuales}
                                                                                >
                                                                                    Siguiente
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="compliance-empty">No se detectaron operaciones inusuales en este mes.</div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        <Row className="g-3 mb-3">
                            {bloques.map((bloque) => (
                                <Col xl={4} md={6} key={bloque.titulo}>
                                    <Card className="compliance-section-card h-100">
                                        <Card.Body>
                                            <div className="compliance-section-title mb-1">{bloque.titulo}</div>
                                            <div className="compliance-section-subtitle mb-3">{bloque.descripcion}</div>
                                            <div className="compliance-kv-grid">
                                                {bloque.valores.map(([label, value]) => renderCampo(label, value, `${bloque.titulo}-${label}`))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <Row className="g-3 mb-3">
                            <Col xl={5}>
                                <Card className="compliance-section-card h-100">
                                    <Card.Body>
                                        <div className="compliance-section-title mb-1">Legajo relacionado</div>
                                        <div className="compliance-section-subtitle mb-3">
                                            Datos documentales y de contacto cargados en el legajo.
                                        </div>
                                        {!detalle.legajoRelacionado ? (
                                            <div className="compliance-empty">No hay legajo relacionado.</div>
                                        ) : (
                                            <div className="compliance-kv-grid">
                                                {legajoDetalle.map(([label, value]) => renderCampo(label, value, `legajo-${label}`))}
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col xl={7}>
                                <Card className="compliance-section-card h-100">
                                    <Card.Body>
                                        <div className="compliance-section-title mb-1">Documentos del legajo</div>
                                        <div className="compliance-section-subtitle mb-3">
                                            Documentacion disponible para descargar desde la ficha.
                                        </div>
                                        {!detalle.documentos || detalle.documentos.length === 0 ? (
                                            <div className="compliance-empty">No hay documentos cargados.</div>
                                        ) : (
                                            <div className="table-responsive">
                                                <Table className="compliance-section-table mb-0" hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Documento</th>
                                                            <th>Tipo</th>
                                                            <th>Tamano</th>
                                                            <th className="text-center">Descargar</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detalle.documentos.map((documento) => (
                                                            <tr key={documento.idDocumento}>
                                                                <td className="compliance-cell-strong">{documento.nombreDocumento}</td>
                                                                <td>{documento.documentoTipo || "-"}</td>
                                                                <td>{formatearBytes(documento.bytes || 0)}</td>
                                                                <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        className="compliance-doc-btn"
                                                                        onClick={() => descargarDocumento(documento)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                                        Descargar
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="g-3 mb-3">
                            <Col lg={6}>
                                <Card className="compliance-section-card h-100">
                                    <Card.Body>
                                        <div className="compliance-section-title mb-1">Seguimiento CRM</div>
                                        <div className="compliance-section-subtitle mb-3">
                                            Gestiones y alertas vinculadas al CUIT o a los CUIT relacionados.
                                        </div>
                                        {!detalle.seguimientos || detalle.seguimientos.length === 0 ? (
                                            <div className="compliance-empty">No hay gestiones de seguimiento asociadas.</div>
                                        ) : (
                                            <div className="table-responsive">
                                                <Table className="compliance-section-table mb-0" hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Fecha</th>
                                                            <th>Estado</th>
                                                            <th>Accion</th>
                                                            <th>Gestor</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detalle.seguimientos.map((seguimiento) => (
                                                            <tr key={seguimiento.id}>
                                                                <td>{formatearFechaHora(seguimiento.fechaGestion)}</td>
                                                                <td>
                                                                    <span className={`compliance-chip ${seguimiento.alertaActiva ? "is-warning" : "is-active"}`}>
                                                                        {seguimiento.estadoResultante || "-"}
                                                                    </span>
                                                                </td>
                                                                <td title={seguimiento.observacion || ""}>
                                                                    {seguimiento.reglaAplicada || seguimiento.observacion || "-"}
                                                                </td>
                                                                <td>{seguimiento.gestor || "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col lg={6}>
                                <Card className="compliance-section-card h-100">
                                    <Card.Body>
                                        <div className="compliance-section-title mb-1">Historial CRM</div>
                                        <div className="compliance-section-subtitle mb-3">
                                            Cambios y observaciones historicas asociadas al registro.
                                        </div>
                                        {!detalle.historial || detalle.historial.length === 0 ? (
                                            <div className="compliance-empty">No hay historial CRM asociado.</div>
                                        ) : (
                                            <div className="table-responsive">
                                                <Table className="compliance-section-table mb-0" hover>
                                                    <thead>
                                                        <tr>
                                                            <th>Fecha</th>
                                                            <th>Tipo</th>
                                                            <th>Usuario</th>
                                                            <th>Observaciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detalle.historial.map((item) => (
                                                            <tr key={item.id}>
                                                                <td>{formatearFechaHora(item.fechaSubida)}</td>
                                                                <td>{item.tipo || "-"}</td>
                                                                <td>{item.usuarioNombre || "-"}</td>
                                                                <td>{item.observaciones || "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                )}
            </div>
        </section>
    );
};

function renderCampo(label, value, key) {
    return (
        <div className="compliance-kv-item" key={key || label}>
            <span className="compliance-kv-label">{label}</span>
            <span className="compliance-kv-value">{value || "-"}</span>
        </div>
    );
}

function formatearFecha(valor) {
    if (!valor) return "-";
    const date = new Date(valor);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("es-AR");
}

function formatearFechaHora(valor) {
    if (!valor) return "-";
    const date = new Date(valor);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("es-AR");
}

function formatearOrigen(valor) {
    if (!valor) return "-";
    return valor.charAt(0).toUpperCase() + valor.slice(1);
}

function construirDomicilioLegajo(legajo) {
    if (!legajo) {
        return "-";
    }

    const partes = [legajo.calle, legajo.numero, legajo.pisoDepto].filter(Boolean);
    return partes.length > 0 ? partes.join(" ") : "-";
}

function resolverEstadoTone(estado) {
    const valor = (estado || "").toLowerCase();

    if (valor.includes("activo") || valor.includes("alta")) {
        return "positive";
    }

    if (valor.includes("revision") || valor.includes("pend")) {
        return "warning";
    }

    if (valor.includes("baja") || valor.includes("suspend")) {
        return "danger";
    }

    return "neutral";
}

function resolverEstadoClass(estado) {
    const tone = resolverEstadoTone(estado);

    if (tone === "positive") return "is-active";
    if (tone === "warning") return "is-warning";
    if (tone === "danger") return "is-danger";
    return "compliance-chip-muted";
}

function formatearBytes(bytes) {
    if (!bytes || bytes <= 0) {
        return "0 B";
    }

    const unidades = ["B", "KB", "MB", "GB"];
    let valor = bytes;
    let indice = 0;

    while (valor >= 1024 && indice < unidades.length - 1) {
        valor /= 1024;
        indice += 1;
    }

    const precision = indice === 0 ? 0 : 1;
    return `${valor.toFixed(precision)} ${unidades[indice]}`;
}

function formatearMoneda(valor) {
    const numero = Number(valor || 0);
    return numero.toLocaleString("es-AR", {
        style: "currency",
        currency: "ARS",
        maximumFractionDigits: 0,
    });
}

function formatearPorcentaje(valor) {
    const numero = Number(valor || 0);
    return numero.toLocaleString("es-AR", { maximumFractionDigits: 0 });
}

function capitalizarTexto(valor) {
    if (!valor) return "-";
    return valor.charAt(0).toUpperCase() + valor.slice(1);
}

function normalizarCacheKey(valor) {
    return String(valor || "sin-terminal")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9_-]/g, "") || "sin-terminal";
}

function obtenerItemsPaginados(items, paginaActual, cantidadPorPagina) {
    const inicio = (paginaActual - 1) * cantidadPorPagina;
    return items.slice(inicio, inicio + cantidadPorPagina);
}

function obtenerRangoPaginas(totalPaginas, paginaActual) {
    const inicio = Math.max(1, paginaActual - 2);
    const fin = Math.min(totalPaginas, inicio + 4);
    const paginas = [];

    for (let pagina = inicio; pagina <= fin; pagina += 1) {
        paginas.push(pagina);
    }

    return paginas;
}

function obtenerNombreArchivo(contentDisposition) {
    if (!contentDisposition) {
        return null;
    }

    const matchUtf = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (matchUtf?.[1]) {
        return decodeURIComponent(matchUtf[1]);
    }

    const matchSimple = contentDisposition.match(/filename="?([^"]+)"?/i);
    return matchSimple?.[1] || null;
}

export default PanelComplianceNuevo;
